import { createElement, useCallback, useEffect, useRef, useState } from "react";
import { isApplePaySupported } from "@/api/apple-pay/api";
import { useTranslation } from "react-i18next";
import {
  getPaymentButtonSlotContainer,
  getPaymentButtonsInShadowDom,
  PaymentButtonSlotIds,
  PaymentButtonSlots,
} from "@/utils/shadow-dom";

export type ApplePayMerchantConfigs = {
  env: string;
  merchantId: string;
  merchantSiteId: string;
};

type ApplePayButtonProps = {
  /** Called when user clicks the button. Runs start-transaction with entered amount, then createApplePayPayment. */
  onApplePayClick: () => Promise<void>;
  /** Called when Apple Pay is not supported or payment/SDK fails. */
  onError?: (error: Error) => void;
  /** Button style: black, white, or white-with-line. */
  buttonStyle?: "black" | "white" | "white-with-line";
  /** Button type: plain, buy, donate, check-out, book, subscribe. */
  type?: "plain" | "buy" | "donate" | "check-out" | "book" | "subscribe";
  /** Locale for the button (e.g. "en-US"). */
  locale?: string;
  /** When true, button is non-interactive and visually dimmed (e.g. payment in progress). */
  disabled?: boolean;
};

export const ApplePayButton = ({
  onApplePayClick,
  onError,
  buttonStyle = "black",
  type: buttonType = "plain",
  locale = "en-US",
  disabled = false,
}: ApplePayButtonProps) => {
  const [isApplePayAvailable, setIsApplePayAvailable] = useState<
    boolean | null
  >(null);
  const buttonRef = useRef<HTMLElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const dummyRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const inShadowDom = getPaymentButtonsInShadowDom();

  useEffect(() => {
    let available = false;
    let errorMessage = t("global.apple_pay_not_supported");
    try {
      available = isApplePaySupported();
    } catch (err) {
      available = false;
      errorMessage =
        err instanceof Error
          ? err.message
          : t("global.apple_pay_not_supported");
      onError?.(new Error(errorMessage));
    }
    setIsApplePayAvailable(available);
  }, [onError, t]);

  const handleClick = useCallback(() => {
    if (disabled) return;
    onApplePayClick().catch((err) =>
      onError?.(err instanceof Error ? err : new Error(String(err)))
    );
  }, [disabled, onApplePayClick, onError]);

  const handleClickRef = useRef(handleClick);
  handleClickRef.current = handleClick;

  // When in shadow DOM: create <apple-pay-button> in the host's light-DOM container so it attaches its shadow root.
  useEffect(() => {
    if (!inShadowDom || isApplePayAvailable !== true || !dummyRef.current)
      return;
    const container = getPaymentButtonSlotContainer(
      dummyRef.current,
      PaymentButtonSlotIds.applePay
    );
    if (!container) return;

    const wrapper = document.createElement("div");
    wrapper.style.width = "100%";
    wrapper.style.minHeight = "56px";
    wrapper.style.height = "100%";

    const btn = document.createElement("apple-pay-button") as HTMLElement;
    btn.setAttribute("buttonstyle", buttonStyle);
    btn.setAttribute("type", buttonType);
    btn.setAttribute("locale", locale);
    btn.style.setProperty("--apple-pay-button-width", "100%");
    btn.style.setProperty("--apple-pay-button-height", "56px");
    btn.style.setProperty("--apple-pay-button-border-radius", "8px");

    wrapper.appendChild(btn);
    container.appendChild(wrapper);
    buttonRef.current = btn;

    wrapperRef.current = wrapper;
    const onClick = () => {
      handleClickRef.current();
    };
    btn.addEventListener("click", onClick);

    return () => {
      wrapperRef.current = null;
      btn.removeEventListener("click", onClick);
      buttonRef.current = null;
      if (wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
    };
  }, [inShadowDom, isApplePayAvailable, buttonStyle, buttonType, locale]);

  // Sync disabled state to shadow DOM wrapper (click is no-op when disabled via handleClickRef).
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    wrapper.style.pointerEvents = disabled ? "none" : "auto";
    wrapper.style.opacity = disabled ? "0.6" : "1";
  }, [disabled]);

  // React tree: attach click listener when not in shadow DOM.
  useEffect(() => {
    if (inShadowDom || isApplePayAvailable !== true) return;
    const el = buttonRef.current;
    if (!el) return;
    el.addEventListener("click", handleClick);
    return () => el.removeEventListener("click", handleClick);
  }, [inShadowDom, isApplePayAvailable, handleClick]);

  const disabledStyle = disabled
    ? { pointerEvents: "none" as const, opacity: 0.6 }
    : undefined;

  if (isApplePayAvailable === false) return null;
  if (isApplePayAvailable !== true) return <div style={{ minHeight: 48 }} />;

  // Shadow DOM: render slot + placeholder; the real button is created in useEffect and appended to the host's light-DOM container.
  if (inShadowDom) {
    return (
      <div style={disabledStyle}>
        <div ref={dummyRef} style={{ display: "none" }} />
        {createElement("slot", { name: PaymentButtonSlots.applePay })}
        <div style={{ minHeight: 56 }} />
      </div>
    );
  }

  // Light DOM: render the button in React as usual.
  return (
    <div style={disabledStyle}>
      <div ref={dummyRef} style={{ display: "none" }} />
      <div
        style={{
          width: "100%",
          minHeight: 56,
          height: "100%",
        }}
      >
        <apple-pay-button
          ref={buttonRef}
          buttonstyle={buttonStyle}
          type={buttonType}
          locale={locale}
          style={{
            ["--apple-pay-button-width" as string]: "100%",
            ["--apple-pay-button-height" as string]: "56px",
            ["--apple-pay-button-border-radius" as string]: "8px",
          }}
        />
      </div>
    </div>
  );
};
